
import ByteEncoder from './lib/ByteEncoder.js'
import ByteDecoder from './lib/ByteDecoder.js'
import Baser from './lib/Baser.js'
import { Uinteger } from './lib/Integer.js'
import { Base64ByteIterator } from './lib/Base64.js'
import { read, write } from './lib/ieee754.js'

const MAX_BYTES = 0x7FFFFFFF
const S_BYTE_VIEW_IS_BYTE_VIEW = Symbol.for('ByteView.isByteView')
const HEX_REGEX = /^(0x)?([\da-fA-F]+)$/
const byteDecoder = new ByteDecoder()
const customInspectSymbol = (typeof Symbol === 'function' && typeof Symbol.for === 'function') // eslint-disable-line dot-notation
  ? Symbol.for('nodejs.util.inspect.custom') // eslint-disable-line dot-notation
  : null

function getNumberPrimitive (input) {
  if (typeof input === 'number' || input === null) {
    return input
  }
  let response

  switch (typeof input) {
    case 'number':
      response = input
      break
    case 'function':
    case 'undefined':
    case 'symbol':
      response = null
      break
    case 'string':
      try {
        response = parseFloat(input)
      } catch (err) {
        response = null
      }
      break
    case 'boolean':
      response = Number(input)
      break
    case 'bigint':
      try {
        response = input.valueOf()
      } catch (err) {
        response = null
      }
      break
    case 'object':
      if (input === null) {
        response = null
      } else if (typeof input[Symbol.toPrimitive] === 'function') {
        try {
          response = input[Symbol.toPrimitive]('number')
          if (typeof response !== 'number') {
            response = null
          }
        } catch (err) {
          response = null
        }
      } else if (typeof input.valueOf === 'function') {
        response = input.valueOf()
      }
      break
    default:
      response = null
      break
  }

  return response
}

export default class ByteView extends Uint8Array {
  #bitOffset = 0
  #size = 0

  /**
   *
   * @param {Uint8Array | ByteEncoder | ArrayBuffer | string | bigint} input
   * @returns {ByteView}
   */
  static from (input, encodingOrOffset, length) {
    const type = typeof input

    if (type === 'string') {
      return fromString(input, (typeof encodingOrOffset !== 'string' ? 'utf8' : encodingOrOffset))
    } else if (type === 'bigint') {
      return fromBigInt(input)
    } else if (type === 'number') {
      throw new TypeError('[ByteView.from] does not expect input = number')
    }

    const buffer = (ByteView.isView(input))
      ? input.buffer
      : ByteView.isNativeBuffer(input)
        ? input
        : null

    if (buffer) {
      if (typeof encodingOrOffset === 'number' && typeof length === 'number') {
        return new ByteView(buffer, encodingOrOffset, length)
      } else if (typeof encodingOrOffset === 'number') {
        return new ByteView(buffer, encodingOrOffset)
      } else {
        return new ByteView(buffer)
      }
    }

    if (isIterable(input) || Array.isArray(input)) {
      return new ByteView(input)
    }

    if (typeof input[Symbol.toPrimitive] === 'function') {
      let value = input[Symbol.toPrimitive]('string')
      if (typeof value === 'string') {
        return fromString(value, (typeof encodingOrOffset !== 'string' ? 'utf8' : encodingOrOffset))
      } else if (
        typeof value === 'number' ||
        typeof (value = input[Symbol.toPrimitive]('number')) === 'number'
      ) {
        return new ByteView(value)
      }
    }

    throw new Error(
      '[ByteView.from]: did not recieve valid type.\n' +
      'Valid types are: string, ArrayBuffer, Uint8Array, bigint, ByteBuffer.'
    )
  }

  static alloc (size) {
    // check for custom number object
    size = getNumberPrimitive(size)

    if (typeof size !== 'number') {
      throw new TypeError(`byteView.alloc expected type "number" recieved ${typeof size}`)
    }

    const length = verifyLength(size)
    if (length < 0) throw new RangeError('size must be greater than 0')

    const byteView = new ByteView(length)
    return byteView
  }

  static allocBits (size, encoding) {
    // check for custom number object
    size = getNumberPrimitive(size)

    if (typeof size !== 'number') {
      throw new TypeError(`byteView.alloc expected type "number" recieved ${typeof size}`)
    }

    const length = verifyLength(size)
    if (length < 0) throw new RangeError('size must be greater than 0')
    let bitOffset = 0
    let responseSize = length

    while (true) {
      if (responseSize % 8 === 0) {
        bitOffset = responseSize - length
        break
      } else {
        ++responseSize
      }
    }

    const realLength = responseSize / 8

    const byteView = new ByteView(bitOffset, length, realLength)
    if (typeof encoding === 'string') {
      byteView.encoding = encoding
    }
    return byteView
  }

  /**
   *
   * @param {Array<ByteView>} list
   * @param {number} length
   * @returns
   */
  static concat (list, length) {
    if (!Array.isArray(list)) {
      throw new TypeError('"list" argument must be an Array of Views')
    }

    if (list.length === 0) {
      return ByteView.alloc(0)
    }

    let index = -1

    if (length === undefined) {
      length = 0
      while (++index < list.length) {
        length += list[index].length
      }
    }

    const bv = ByteView.alloc(length)
    let pos = 0
    index = -1

    while (++index < list.length) {
      let byteView = list[index]
      if (ByteView.isView(byteView)) {
        if (pos + byteView.length > bv.length) {
          if (!ByteView.isByteView(byteView)) {
            byteView = ByteView.from(byteView.buffer, byteView.byteOffset, byteView.byteLength)
          }
          byteView.copy(bv, pos)
        } else {
          bv.set(byteView, pos)
        }
      } else if (!ByteView.isByteView(byteView)) {
        throw new TypeError('"list" argument must be an Array of Buffers')
      } else {
        byteView.copy(bv, pos)
      }
      pos += byteView.length
    }

    return bv
  }

  get [S_BYTE_VIEW_IS_BYTE_VIEW] () {
    return true
  }

  static isByteView (byteView) {
    return (
      byteView !== null &&
      byteView !== undefined &&
      typeof byteView[S_BYTE_VIEW_IS_BYTE_VIEW] === 'boolean' &&
      byteView[S_BYTE_VIEW_IS_BYTE_VIEW] === true
    )
  }

  static isView (input) {
    return ArrayBuffer.isView(input) || ByteView.isByteView(input)
  }

  static isNativeBuffer (input) {
    return (
      isInstance(input, ArrayBuffer) ||
      (
        typeof SharedArrayBuffer !== 'undefined' &&
        isInstance(input, SharedArrayBuffer)
      )
    )
  }

  static serialize (input) {
    return ByteView.from(JSON.stringify(input), 'utf8').toString('base64')
  }

  static unserialize (input) {
    return JSON.parse(ByteView.from(input, 'base64').toString())
  }

  static byteLength (string, encoding = 'utf8') {
    if (
      ByteView.isNativeBuffer(string) ||
      ByteView.isView(string)
    ) {
      return string.byteLength
    }

    if (typeof string !== 'string') {
      throw new TypeError(
        'The "string" argument must be one of type string, Buffer, or ArrayBuffer. ' +
        'Received type ' + typeof string
      )
    }

    const { length } = string
    let res = 0
    let padding = 0

    switch (encoding) {
      case 'utf8':
      case 'utf-8':
        res = byteLengthFromString(string)
        break
      case 'base32':
      case 'base32hex':
      case 'base32crockford':
        padding = string.indexOf('=')
        res = (((length - (
          padding === -1 ? 0 : length - padding
        )) * 5) >> 0x3) | 0
        break
      case 'base64':
      case 'base64url':
        padding = string.indexOf('=')
        res = (((length - (
          padding === -1 ? 0 : length - padding
        )) * 6) >> 0x3) | 0
        break
      case 'ascii':
      case 'latin1':
      case 'binary':
        res = length
        break
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        res = length * 2
        break
      case 'hex':
        res = length >>> 1
        break
      default:
        res = byteLengthFromString(string)
        break
    }

    return res
  }

  static isOSLittleEndian = (() => {
    const u8 = new Uint8Array(2)
    u8[0] = 0xFF
    return new Uint16Array(u8.buffer)[0] === 255
  })()

  constructor (buffer, byteOffset, length) {
    const isBits = (
      typeof buffer === 'number' &&
      typeof byteOffset === 'number' &&
      typeof length === 'number'
    )

    if (isBits) {
      super(length)
      this.#bitOffset = buffer
      this.#size = byteOffset
    } else {
      super(buffer, byteOffset, length)
      this.#size = this.length
    }
  }

  get size () {
    return this.#size
  }

  get bitOffset () {
    return this.#bitOffset
  }

  swap16 () {
    const { length } = this
    let index = -1

    while (++index < length) {
      const ai = index
      const bi = ++index
      const a = this[ai]
      this[ai] = this[bi]
      this[bi] = a
    }

    return this
  }

  swap32 () {
    const { length } = this
    let index = -1

    while (++index < length) {
      const ai = index
      const bi = ++index
      const ci = ++index
      const di = ++index
      const a = this[ai]
      const b = this[bi]
      this[ai] = this[di]
      this[bi] = this[ci]
      this[ci] = b
      this[di] = a
    }

    return this
  }

  swap64 () {
    const { length } = this
    let index = -1

    while (++index < length) {
      const ai = index
      const bi = ++index
      const ci = ++index
      const di = ++index
      const ei = ++index
      const fi = ++index
      const gi = ++index
      const hi = ++index
      const a = this[ai]
      const b = this[bi]
      const c = this[ci]
      const d = this[di]
      this[ai] = this[hi]
      this[bi] = this[gi]
      this[ci] = this[fi]
      this[di] = this[ei]
      this[ei] = d
      this[fi] = c
      this[gi] = b
      this[hi] = a
    }

    return this
  }

  getRGB (index) {
    const { length } = this
    let offset = index * 3

    if ((offset + 3) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    return [this[offset], this[++offset], this[++offset]]
  }

  getColorHex (index) {
    const hexView = new ByteView(3)
    let offset = index * 3

    if ((offset + 3) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    for (let index = 0; index < 3; index++) {
      hexView[index] = array[offset + index]
    }

    return hexView.toString('hex')
  }

  getBit (index) {
    index += this.#bitOffset
    const offset = index & 0x7
    return (this[index >> 3] >> (7 - offset)) & 0x1
  }

  setBit (index, value) {
    index += this.#bitOffset
    const offset = index & 0x7
    const thisIndex = index >> 3
    if (value) {
      this[thisIndex] |= (0x80 >> offset)
    } else {
      this[thisIndex] &= ~(0x80 >> offset)
    }

    return ++index
  }

  sliceBits (start, end) {
    let index = start - 1
    index += this.#bitOffset
    let bits = 0

    while (++index < end) {
      const offset = index & 0x7
      const bit = (this[index >> 3] >> (7 - offset)) & 1
      bits = (bits << 0x1) | bit
    }

    return bits
  }

  getUint5 (index) {
    /** amount of bits before index on bitArray */
    const previousBitsUsed = (index * 5)
    /** find where bitArray index falls on this */
    const thisIndex = previousBitsUsed >> 0x3
    /** amount of bits before index on this */
    const previousBits = (thisIndex + 1) << 0x3
    /** amount of bits currently selected & not used */
    let currentBits = (previousBits - previousBitsUsed) & 0x7

    // return undefined if out of range
    if (
      thisIndex >= this.length ||
      // index >= length of bitArray
      index >= ((this.length << 0x3) / 5 | 0)
    ) {
      return undefined
    }

    let byte = this[thisIndex]
    currentBits += 8

    if (currentBits !== 8) {
      byte = (byte << 0x8) | (this[thisIndex + 1] || 0)
    }

    return (byte >>> (currentBits - 5)) & 0x1F
  }

  getUint6 (index) {
    /** amount of bits before index on bitArray */
    const previousBitsUsed = (index * 6)
    /** find where bitArray index falls on this */
    const thisIndex = previousBitsUsed >> 0x3
    /** amount of bits before index on this */
    const previousBits = (thisIndex + 1) << 0x3
    /** amount of bits currently selected & not used */
    let currentBits = (previousBits - previousBitsUsed) & 0x7

    // return undefined if out of range
    if (
      thisIndex >= this.length ||
      // index >= length of bitArray
      index >= ((this.length << 0x3) / 6 | 0)
    ) {
      return undefined
    }

    let byte = this[thisIndex]
    currentBits += 8

    if (currentBits !== 8) {
      byte = (byte << 0x8) | (this[thisIndex + 1] || 0)
    }

    return (byte >>> (currentBits - 6)) & 0x3F
  }

  getUint8 (offset) {
    offset >>>= 0
    const { length } = this

    if ((offset + 1) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    return this[offset]
  }

  setUint8 (offset, value) {
    offset >>>= 0
    const { length } = this

    if ((offset + 1) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    this[offset] = value & 0xFF

    return ++offset
  }

  getUint16 (offset, littleEndian = false) {
    offset >>>= 0
    const { length } = this

    if ((offset + 2) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    return littleEndian
      ? this[offset] | (this[offset + 1] << 8)
      : this[offset + 1] | (this[offset] << 8)
  }

  setUint16 (offset, value, littleEndian = false) {
    offset >>>= 0

    if (littleEndian) {
      this[offset] = (value & 0xFF)
      this[++offset] = (value >>> 8)
    } else {
      this[offset] = (value >>> 8)
      this[++offset] = (value & 0xFF)
    }

    return ++offset
  }

  getUint24 (offset, littleEndian = false) {
    offset >>>= 0
    const { length } = this

    if ((offset + 3) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    return littleEndian
      ? (
          this[offset] |
          (this[++offset] << 8) |
          (this[++offset] << 16)
        )
      : (
          (this[offset] << 16) |
          (this[++offset] << 8) |
          this[++offset]
        )
  }

  setUint24 (offset, value, littleEndian = false) {
    value = +value
    offset >>>= 0
    const { length } = this

    if ((offset + 3) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }
    if (value > 0xFFFFFFFF) {
      throw new RangeError('value is beyond the max integer.')
    }

    if (littleEndian) {
      this[offset + 2] = (value >>> 16)
      this[offset + 1] = (value >>> 8)
      this[offset] = (value & 0xFF)
    } else {
      this[offset] = (value >>> 16)
      this[offset + 1] = (value >>> 8)
      this[offset + 2] = (value & 0xFF)
    }

    return offset + 3
  }

  getUint32 (offset, littleEndian = false) {
    offset >>>= 0
    const { length } = this

    if ((offset + 4) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    return littleEndian
      ? (
          (this[offset]) |
          (this[++offset] << 8) |
          (this[++offset] << 16)
        ) + (this[++offset] * 0x1000000)
      : (this[offset] * 0x1000000) + (
          (this[++offset] << 16) |
          (this[++offset] << 8) |
          (this[++offset])
        )
  }

  setUint32 (offset, value, littleEndian = false) {
    value = +value
    offset >>>= 0
    const { length } = this

    if ((offset + 4) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }
    if (value > 0xFFFFFFFF) {
      throw new RangeError('value is beyond the max integer.')
    }

    if (littleEndian) {
      this[offset + 3] = (value >>> 24)
      this[offset + 2] = (value >>> 16)
      this[offset + 1] = (value >>> 8)
      this[offset] = (value & 0xFF)
    } else {
      this[offset] = (value >>> 24)
      this[offset + 1] = (value >>> 16)
      this[offset + 2] = (value >>> 8)
      this[offset + 3] = (value & 0xFF)
    }

    return offset + 4
  }

  getBigUint64 (offset, littleEndian = false) {
    offset >>>= 0
    const { length } = this
    let high = 0
    let low = 0

    if ((offset + 8) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    if (littleEndian) { // OS is littleEndian
      low = (this[offset] | (this[++offset] << 8) | (this[++offset] << 16)) +
        (this[++offset] * 0x1000000)
      high = (this[++offset] | (this[++offset] << 8) | (this[++offset] << 16)) +
        (this[++offset] * 0x1000000)
    } else { // OS is bigEndian
      high = (this[offset] * 0x1000000) +
        ((this[++offset] << 16) | (this[++offset] << 8) | this[++offset])
      low = (this[++offset] * 0x1000000) +
        ((this[++offset] << 16) | (this[++offset] << 8) | this[++offset])
    }

    return (BigInt(high >>> 0) << BigInt(32)) | BigInt(low >>> 0)
  }

  setBigUint64 (offset, value, littleEndian = false) {
    const min = BigInt(0)
    const max = BigInt('0xffffffffffffffff')

    if (value > max || value < min) {
      throw new RangeError(
        (value > max
          ? 'value exceeds valid range. '
          : 'value does not meet minimum range. '
        ),
        value
      )
    }

    if ((offset + 8) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    if (littleEndian) {
      let lo = Number(value & BigInt(0xffffffff))
      this[offset] = lo
      lo = lo >> 8
      this[++offset] = lo
      lo = lo >> 8
      this[++offset] = lo
      lo = lo >> 8
      this[++offset] = lo
      let hi = Number(value >> BigInt(32) & BigInt(0xffffffff))
      this[offset] = hi
      hi = hi >> 8
      this[++offset] = hi
      hi = hi >> 8
      this[++offset] = hi
      hi = hi >> 8
      this[++offset] = hi
    } else {
      let lo = Number(value & BigInt(0xffffffff))
      this[offset + 7] = lo
      lo = lo >> 8
      this[offset + 6] = lo
      lo = lo >> 8
      this[offset + 5] = lo
      lo = lo >> 8
      this[offset + 4] = lo
      let hi = Number(value >> BigInt(32) & BigInt(0xffffffff))
      this[offset + 3] = hi
      hi = hi >> 8
      this[offset + 2] = hi
      hi = hi >> 8
      this[offset + 1] = hi
      hi = hi >> 8
      this[offset] = hi
      offset += 7
    }

    return ++offset
  }

  getInt (offset, byteLength, littleEndian = false) {
    offset = offset >>> 0
    byteLength = byteLength >>> 0

    if ((offset + byteLength) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    let val = 0
    let mul = 1
    let i = 0

    if (littleEndian) {
      val = this[offset]
      while (++i < byteLength && (mul *= 0x100)) {
        val += this[offset + i] * mul
      }
    } else {
      i = byteLength
      val = this[offset + --i]
      while (i > 0 && (mul *= 0x100)) {
        val += this[offset + --i] * mul
      }
    }

    mul *= 0x80

    if (val >= mul) val -= Math.pow(2, 8 * byteLength)

    return val
  }

  getInt8 (offset) {
    offset = ~~offset
    const { length } = this

    if ((offset + 1) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }
    if (!(this[offset] & 0x80)) {
      return (this[offset])
    }

    return ((0xFF - this[offset] + 1) * -1)
  }

  setInt8 (offset, value) {
    offset = Uinteger(offset)
    value = Uinteger(value, 8)
    const { length } = this

    if ((offset + 1) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    this[offset] = (value > 0x7F) ? value - 0x100 : value

    return ++offset
  }

  getInt16 (offset, littleEndian = false) {
    offset >>>= 0
    const { length } = this

    if ((offset + 2) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    const value = littleEndian
      ? this[offset] | (this[offset + 1] << 8)
      : this[offset + 1] | (this[offset] << 8)

    return (value & 0x8000) ? value | 0xFFFF0000 : value
  }

  setInt16 (offset, value, littleEndian = false) {
    offset >>>= 0

    if ((offset + 2) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    if (littleEndian) {
      this[offset] = (value & 0xFF)
      this[++offset] = (value >>> 8)
    } else {
      this[offset] = (value >>> 8)
      this[++offset] = (value & 0xFF)
    }

    return ++offset
  }

  getInt24 (offset, littleEndian = false) {
    offset >>>= 0

    if ((offset + 3) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    const value = littleEndian
      ? (this[offset]) |
        (this[++offset] << 8) |
        (this[++offset] << 16)
      : (this[offset] << 16) |
        (this[++offset] << 8) |
        (this[++offset])

    return (value & 0x800000) ? (0xffffff - value + 1) * -1 : value
  }

  setInt24 (offset, value, littleEndian = false) {
    offset >>>= 0

    if ((offset + 4) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    if (littleEndian) {
      this[offset] = (value & 0xff)
      this[++offset] = (value >>> 8)
      this[++offset] = (value >>> 16)
    } else {
      if (value < 0) value = 0xffffff + value + 1
      this[offset] = (value >>> 16)
      this[++offset] = (value >>> 8)
      this[++offset] = (value & 0xff)
    }

    return ++offset
  }

  getInt32 (offset, littleEndian = false) {
    offset >>>= 0

    if ((offset + 4) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    return littleEndian
      ? (this[offset]) |
        (this[++offset] << 8) |
        (this[++offset] << 16) |
        (this[++offset] << 24)
      : (this[offset] << 24) |
        (this[++offset] << 16) |
        (this[++offset] << 8) |
        (this[++offset])
  }

  setInt32 (offset, value, littleEndian = false) {
    offset >>>= 0

    if ((offset + 4) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    if (littleEndian) {
      this[offset] = (value & 0xff)
      this[++offset] = (value >>> 8)
      this[++offset] = (value >>> 16)
      this[++offset] = (value >>> 24)
    } else {
      if (value < 0) value = 0xffffffff + value + 1
      this[offset] = (value >>> 24)
      this[++offset] = (value >>> 16)
      this[++offset] = (value >>> 8)
      this[++offset] = (value & 0xff)
    }

    return ++offset
  }

  getBigInt64 (offset, littleEndian = false) {
    offset >>>= 0
    const { length } = this

    if ((offset + 8) > length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    const first = this[offset]
    const last = this[offset + 7]

    if (littleEndian) { // OS is littleEndian
      const val = this[offset + 4] +
        this[offset + 5] * 2 ** 8 +
        this[offset + 6] * 2 ** 16 +
        (last << 24) // Overflow

      return (
        (BigInt(val) << BigInt(32)) +
        BigInt(
          first +
          this[++offset] * 2 ** 8 +
          this[++offset] * 2 ** 16 +
          this[++offset] * 2 ** 24
        )
      )
    } else { // OS is bigEndian
      const val = (first << 24) + // Overflow
        this[++offset] * 2 ** 16 +
        this[++offset] * 2 ** 8 +
        this[++offset]

      return (
        (BigInt(val) << BigInt(32)) +
        BigInt(
          this[++offset] * 2 ** 24 +
          this[++offset] * 2 ** 16 +
          this[++offset] * 2 ** 8 +
          last
        )
      )
    }
  }

  setBigInt64 (offset, value, littleEndian = false) {
    const min = -BigInt('0x8000000000000000')
    const max = BigInt('0x7fffffffffffffff')

    if (value > max || value < min) {
      throw new RangeError(
        (value > max
          ? 'value exceeds valid range. '
          : 'value does not meet minimum range. '
        ),
        value
      )
    }

    if ((offset + 8) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    if (littleEndian) {
      let lo = Number(value & BigInt(0xffffffff))
      this[offset] = lo
      lo = lo >> 8
      this[++offset] = lo
      lo = lo >> 8
      this[++offset] = lo
      lo = lo >> 8
      this[++offset] = lo
      let hi = Number(value >> BigInt(32) & BigInt(0xffffffff))
      this[offset] = hi
      hi = hi >> 8
      this[++offset] = hi
      hi = hi >> 8
      this[++offset] = hi
      hi = hi >> 8
      this[++offset] = hi
    } else {
      let lo = Number(value & BigInt(0xffffffff))
      this[offset + 7] = lo
      lo = lo >> 8
      this[offset + 6] = lo
      lo = lo >> 8
      this[offset + 5] = lo
      lo = lo >> 8
      this[offset + 4] = lo
      let hi = Number(value >> BigInt(32) & BigInt(0xffffffff))
      this[offset + 3] = hi
      hi = hi >> 8
      this[offset + 2] = hi
      hi = hi >> 8
      this[offset + 1] = hi
      hi = hi >> 8
      this[offset] = hi
      offset += 7
    }

    return ++offset
  }

  setBigInt (offset, number) {
    const start = offset
    const bit32 = BigInt(32)
    const min32 = BigInt(0xffffffff)
    let left = number < BigInt(0) ? -number : number
    let setValue = number

    while (left > min32) {
      offset = this.setInt32(offset, Number(setValue & min32), true)
      left >>= bit32
      setValue >>= bit32
    }

    left = Number(left)
    setValue = Number(setValue)

    while (left > 0xffff) {
      offset = this.setInt16(offset, setValue & 0xffff, true)
      left >>= 16
      setValue >>= 16
    }

    while (left > 0) {
      offset = this.setInt8(offset, setValue & 0xff)
      left >>= 8
      setValue >>= 8
    }

    return offset - start
  }

  getBigInt (offset, byteLength) {
    const bits = BigInt(this.BYTES_PER_ELEMENT * 8)
    let index = 0
    const bv = this.slice(offset, offset + byteLength).reverse()
    let res = BigInt(bv[index] & 0x7f)

    if (bv[index] & 0x80) {
      res -= BigInt(0x80)// Treat most-significant bit as -2^i instead of 2^i
    }

    while (++index < byteLength) {
      res = (res << bits) + BigInt(bv.getUint8(index))
    }

    return res
  }

  getFloat32 (offset, littleEndian = false) {
    offset >>>= 0

    if ((offset + 4) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    return read(this, offset, littleEndian, false)
  }

  setFloat32 (offset, value, littleEndian = false) {
    offset >>>= 0

    if ((offset + 4) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    return write(this, value, offset, littleEndian, false)
  }

  getFloat64 (offset, littleEndian = false) {
    offset >>>= 0

    if ((offset + 8) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    return read(this, offset, littleEndian, true)
  }

  setFloat64 (offset, value, littleEndian = false) {
    offset >>>= 0

    if ((offset + 8) > this.length) {
      throw new RangeError('Integer invalid: offset is beyond ByteView.length')
    }

    write(this, value, offset, littleEndian, true)

    return offset + 8
  }

  getInt6 (index) {
    const { length: u8Length } = this
    const u6Length = (u8Length << 3) / 6 | 0
    const u6Bits = (index * 6)
    const u8Index = u6Bits >> 3
    const u8Bits = (u8Index + 1) << 3
    let bits = (u8Bits - u6Bits) & 0x7

    if (u8Index >= u8Length || index >= u6Length) {
      return undefined
    }

    let byte = this[u8Index]
    bits += 8

    if (bits !== 8) {
      const nextU8Byte = this[u8Index + 1] || 0
      byte = (byte << 0x8) | nextU8Byte
    }

    const uByte = (byte >>> (bits - 6)) & 0x3F

    return (uByte > 0x1F) ? uByte - 0x3F : uByte
  }

  toJSON () {
    return {
      type: 'ByteView',
      data: this.toString('base64')
    }
  }

  * [Symbol.iterator] () {
    let index = -1
    const length = this.byteLength

    while (++index < length) {
      yield this[index]
    }
  }

  [Symbol.for('nodejs.util.inspect.custom')] () {
    return this.inspect().replace(/\((\d+)\)/, '(\u001b[33m$1\u001b[0m)')
  }

  get [Symbol.toStringTag] () {
    return 'ByteView'
  }

  inspect () {
    const arr = Baser.base16.encode(this, ' ').split(' ')
    return `<ByteView(${this.byteLength}) ${
      arr.length > 80
        ? arr.slice(0, 81).join(' ') + ' ...' + String(arr.length - 80) + ' more bytes'
        : arr.join(' ')
    } />`
  }

  toString (encoding = 'utf8') {
    let response = ''

    switch (encoding) {
      case 'utf-8':
      case 'utf8':
        response = byteDecoder.decode(this)
        break
      case 'hex':
        response = Baser.base16.encode(this)
        break
      case 'base32':
        response = Baser.base32.encode(this)
        break
      case 'base32hex':
        response = Baser.base32hex.encode(this)
        break
      case 'base32crockford':
        response = Baser.base32crockford.encode(this)
        break
      case 'base64':
        response = Baser.base64.encode(this)
        break
      case 'base64url':
        response = Baser.base64url.encode(this)
        break
      case 'ascii':
        response = asciiSlice(this)
        break
      case 'latin1':
      case 'binary':
        response = latin1Slice(this)
        break
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        response = utf16leSlice(this)
        break
      case 'utf16be':
      case 'utf-16be':
        response = utf16beSlice(this)
        break
      default:
        response = byteDecoder.decode(this)
        break
    }

    return response
  }

  subarray (start, end) {
    const { length } = this
    start = ~~start
    end = end === undefined ? length : ~~end
    const newLength = end - start

    if (end > length) {
      throw new RangeError(String(end) + ' is out of range of ByteView')
    }

    return new ByteView(
      this.buffer,
      this.byteOffset + start * this.BYTES_PER_ELEMENT,
      newLength
    )
  }

  copy (target, targetStart, start, end) {
    if (!ByteView.isByteView(target)) throw new TypeError('argument should be a Buffer')
    if (!start) start = 0
    if (!end && end !== 0) end = this.length
    if (targetStart >= target.length) targetStart = target.length
    if (!targetStart) targetStart = 0
    if (end > 0 && end < start) end = start

    // Copy 0 bytes; we're done
    if (end === start) return 0
    if (target.length === 0 || this.length === 0) return 0

    // Fatal error conditions
    if (targetStart < 0) {
      throw new RangeError('targetStart out of bounds')
    }
    if (start < 0 || start >= this.length) throw new RangeError('Index out of range')
    if (end < 0) throw new RangeError('sourceEnd out of bounds')

    // Are we oob?
    if (end > this.length) end = this.length
    if (target.length - targetStart < end - start) {
      end = target.length - targetStart + start
    }

    const length = end - start

    if (this === target && typeof Uint8Array.prototype.copyWithin === 'function') {
      // Use built-in when available, missing from IE11
      this.copyWithin(targetStart, start, end)
    } else {
      Uint8Array.prototype.set.call(
        target,
        this.subarray(start, end),
        targetStart
      )
    }

    return length
  }

  fill (val, start, end, encoding) {
    const type = typeof val

    if (type === 'string') {
      if (typeof start === 'string') {
        encoding = start
        start = 0
        end = this.length
      } else if (typeof end === 'string') {
        encoding = end
        end = this.length
      }
      if (encoding !== undefined && typeof encoding !== 'string') {
        throw new TypeError('encoding must be a string')
      }
      if (val.length === 1 && encoding === 'utf8') {
        const code = val.charCodeAt(0)
        if (code < 128) {
          // Fast path: If `val` fits into a single byte, use that numeric value.
          val = code
        }
      }
    } else if (type === 'number') {
      val = val & 255
    } else if (!val || type === 'boolean') {
      val = Number(val)
    }

    // Invalid ranges are not set to a default, so can range check early.
    if (start < 0 || this.length < start || this.length < end) {
      throw new RangeError('Out of range index')
    }

    if (end <= start) {
      return this
    }

    start = start >>> 0
    end = end === undefined ? this.length : end >>> 0

    let i
    if (typeof val === 'number') {
      i = start - 1
      while (++i < end) this[i] = val
    } else {
      const bytes = ByteView.isByteView(val)
        ? val
        : ByteView.from(val, encoding)
      const len = bytes.length
      if (len === 0) {
        throw new TypeError('The value "' + val +
          '" is invalid for argument "value"')
      }
      i = -1
      const max = end - start
      while (++i < max) this[i + start] = bytes[i % len]
    }

    return this
  }

  equals (byteView) {
    if (!ByteView.isByteView(byteView)) throw new TypeError('Argument [byteView] must be a ByteView')
    if (this === byteView) return true
    return this.compare(byteView) === 0
  }

  compare (target, start, end, thisStart, thisEnd) {
    if (ByteView.isView(target)) {
      target = new ByteView(target, target.offset, target.byteLength)
    }
    if (!ByteView.isByteView(target)) {
      throw new TypeError(
        'The "target" argument must be one of type ByteView or Uint8Array. ' +
        'Received type ' + (typeof target)
      )
    }

    thisStart = thisStart < 0 ? ~thisStart + 1 : ~~thisStart
    thisEnd = thisEnd ? ~~thisEnd : this.length

    if (start < 0 || end > target.length || thisStart < 0 || thisEnd > this.length) {
      throw new RangeError('out of range index')
    }

    if (thisStart >= thisEnd && start >= end) {
      return 0
    }
    if (thisStart >= thisEnd) {
      return -1
    }
    if (start >= end) {
      return 1
    }

    start >>>= 0
    end >>>= 0
    thisStart >>>= 0
    thisEnd >>>= 0

    if (this === target) return 0

    let x = thisEnd - thisStart
    let y = end - start
    const len = Math.min(x, y)

    const thisCopy = this.slice(thisStart, thisEnd)
    const targetCopy = target.slice(start, end)

    let index = -1
    while (++index < len) {
      if (thisCopy[index] !== targetCopy[index]) {
        x = thisCopy[index]
        y = targetCopy[index]
        break
      }
    }

    if (x < y) return -1
    if (y < x) return 1
    return 0
  }

  write (string, offset, length, encoding) {
    if (offset === undefined) {
      encoding = 'utf8'
      length = this.length
      offset = 0
    } else if (length === undefined && typeof offset === 'string') {
      encoding = offset
      length = this.length
      offset = 0
    } else if (isFinite(offset)) {
      offset = offset >>> 0
      if (isFinite(length)) {
        length = length >>> 0
        if (encoding === undefined) encoding = 'utf8'
      } else {
        encoding = length
        length = undefined
      }
    }

    const remaining = this.length - offset
    if (length === undefined || length > remaining) length = remaining

    if ((string.length > 0 && (length < 0 || offset < 0)) || offset > this.length) {
      throw new RangeError('Attempt to write outside buffer bounds')
    }

    if (!encoding) encoding = 'utf8'

    switch (encoding) {
      case 'hex':
        offset = hexWrite(this, string, offset, length)
        break
      case 'utf8':
      case 'utf-8':
        offset = utf8Write(this, string, offset, length)
        break
      case 'ascii':
      case 'latin1':
      case 'binary':
        offset = asciiWrite(this, string, offset, length)
        break
      case 'base64':
        // Warning: maxLength not taken into account in base64Write
        offset = base64Write(this, string, offset, length)
        break
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        offset = ucs2Write(this, string, offset, length)
        break
      default:
        throw new TypeError('Unknown encoding: ' + encoding)
        /* eslint-disable-next-line */
        break
    }

    return offset
  }

  toBigInt () {
    const bits = BigInt(this.BYTES_PER_ELEMENT * 8)
    let index = -1
    const { length } = this
    let res = 0n

    while (++index < length) {
      res = (res << bits) + BigInt(this[index])
    }

    return res
  }

  /**
   *
   * @param {number} targetLength
   * @returns {ByteView}
   */
  padStart (targetLength) {
    if (this.byteLength < targetLength) {
      const tmp = new ByteView(targetLength)
      tmp.fill(0, 0, targetLength - this.byteLength)
      tmp.set(this, targetLength - this.byteLength)
      return tmp
    }
    return this
  }
}

function verifyLength (length) {
  if (length >= MAX_BYTES) {
    throw new RangeError(
      'length is larger than maximum ' +
      'size: 0x' + MAX_BYTES.toString(16) + ' bytes'
    )
  }

  return length
}

function isIterable (obj) {
  return (
    obj != null &&
    typeof obj[Symbol.iterator] === 'function'
  )
}

function fromString (string, encoding) {
  let output = null

  switch (encoding) {
    case 'utf8':
    case 'utf-8':
      output = new ByteView(new ByteEncoder(string))
      break
    case 'base32':
      output = Baser.base32.decode(string)
      break
    case 'base32hex':
      output = Baser.base32hex.decode(string)
      break
    case 'base32crockford':
      output = Baser.base32crockford.decode(string)
      break
    case 'base64':
      output = new ByteView(new Base64ByteIterator(string, 'base64'))
      break
    case 'base64url':
      output = new ByteView(new Base64ByteIterator(string, 'base64url'))
      break
    case 'ascii':
    case 'latin1':
    case 'binary':
      output = new ByteView(asciiToBytes(string))
      break
    case 'ucs2':
    case 'ucs-2':
    case 'utf16le':
    case 'utf-16le':
      output = new ByteView(utf16leToBytes(string))
      break
    case 'hex':
      output = Baser.base16.decode(string)
      break
    default:
      output = new ByteView(new ByteEncoder(string))
      break
  }

  return output
}

function byteLengthFromString (string) {
  const be = new ByteEncoder(string)
  let length = 0

  for (const _ of be) {
    ++length
  }

  return length
}

function fromBigInt (input) {
  const hexString = input.toString(16)
  if (hexString === undefined) {
    throw RangeError('hexStr cannot undefined')
  }
  const hexMatch = hexString.match(HEX_REGEX)
  if (hexMatch == null) {
    throw RangeError('hexStr must be a hexadecimal string, e.g. \'0x124fe3a\' or \'0214f1b2\'')
  }
  let hex = hexMatch[2]
  hex = (hex.length % 2 === 0) ? hex : '0' + hex
  return ByteView.from(hex, 'hex')
}

function asciiSlice (byteView, start, end) {
  let res = ''
  const { length } = byteView
  end = end > length ? length : end
  start = start === undefined ? 0 : start
  let index = start - 1

  while (++index < length) {
    res += String.fromCharCode(byteView[index] & 0x7F)
  }

  return res
}

function latin1Slice (byteView, start, end) {
  let res = ''
  const { length } = byteView
  end = end > length ? length : end
  start = start === undefined ? 0 : start
  let index = start - 1

  while (++index < length) {
    res += String.fromCharCode(byteView[index])
  }

  return res
}

function utf16leSlice (byteView, start, end) {
  start = start === undefined ? 0 : start
  const bytes = byteView.subarray(start, end)
  let res = ''
  const { length } = bytes
  end = end > length ? length : end
  let index = start - 1
  const tester = length - 1

  while (++index < tester) {
    res += String.fromCharCode(bytes[index] + (bytes[++index] * 256))
  }

  return res
}

function utf16beSlice (byteView, start, end) {
  start = start === undefined ? 0 : start
  const bytes = byteView.subarray(start, end)
  let res = ''
  const { length } = bytes
  end = end > length ? length : end
  let index = start - 1
  const tester = length - 1

  while (++index < tester) {
    res += String.fromCharCode(bytes.getUint16(index))
    ++index
  }

  return res
}

function hexWrite (buf, string, offset, length) {
  offset = Number(offset) || 0
  const remaining = buf.length - offset
  if (!length) {
    length = remaining
  } else {
    length = Number(length)
    if (length > remaining) {
      length = remaining
    }
  }

  const strLen = string.length

  if (length > strLen / 2) {
    length = strLen / 2
  }
  let i
  for (i = 0; i < length; ++i) {
    const parsed = parseInt(string.substr(i * 2, 2), 16)
    if (isNaN(parsed)) return i
    buf[offset + i] = parsed
  }
  return i
}

function utf8Write (buf, string, offset, length) {
  let units = buf.length - offset
  const iterator = new ByteEncoder(string)
  let index = 0

  for (const byte of iterator) {
    if (
      index >= length ||
      --units < 0 ||
      (index + offset >= buf.length)
    ) {
      break
    }
    buf[index + offset] = byte
    ++index
  }

  return index
}

function asciiWrite (buf, string, offset, length) {
  return copyInto(asciiToBytes(string), buf, offset, length)
}

function base64Write (buf, string, offset, length) {
  const iterator = new Base64ByteIterator(string, 'base64')
  let index = 0

  for (const byte of iterator) {
    if (
      index >= length ||
      (index + offset >= buf.length)
    ) {
      break
    }
    buf[index + offset] = byte
    ++index
  }

  return index
}

function ucs2Write (buf, string, offset, length) {
  return copyInto(utf16leToBytes(string, buf.length - offset), buf, offset, length)
}

function copyInto (source, destination, offset, length) {
  let index = -1
  while (++index < length) {
    if ((index + offset >= destination.length) || (index >= source.length)) break
    destination[index + offset] = source[index]
  }
  return index
}

function asciiToBytes (str) {
  const byteArray = []
  for (let i = 0; i < str.length; ++i) {
    // Node's code seems to be doing this and not & 0x7F..
    byteArray.push(str.charCodeAt(i) & 0xFF)
  }
  return byteArray
}

function utf16leToBytes (str, units) {
  let c, hi, lo
  const byteArray = []
  for (let i = 0; i < str.length; ++i) {
    if ((units -= 2) < 0) break

    c = str.charCodeAt(i)
    hi = c >> 8
    lo = c % 256
    byteArray.push(lo)
    byteArray.push(hi)
  }

  return byteArray
}

/**
 *
 * @param {any} input
 * @param {any} instance
 * @returns {boolean}
 */
function isInstance (input, instance) {
  return (
    input instanceof instance ||
    (input.constructor && input.constructor.name === instance.name) ||
    toType(input) === instance.name
  )
}

function toType (input) {
  const type = typeof input
  let response = type

  switch (type) {
    case 'function':
      response = input.name || 'function'
      break
    case 'object':
      response = nativeObjectToString(input)
      break
    case 'number':
      response = nativeObjectToString(input)
      response = response === 'Number'
        ? response.toLowerCase()
        : response
      break
    case 'bigint':
    case 'boolean':
    case 'string':
    case 'symbol':
    case 'undefined':
    default:
      response = type
      break
  }

  return response
}

function nativeObjectToString (input) {
  return Object.prototype.toString.call(input).slice(8, -1)
}
